import { skipToken } from "@reduxjs/toolkit/query";
import {
  isPlaybookCriterionNode,
  isPlaybookSectionNode,
  type PlaybookCriterionNode,
  type PlaybookId,
  type PlaybookSectionNode,
} from "@repo/app-types/entities/playbook";
import type {
  CriterionGrade,
  RecordingGradingAssessment,
  SectionGrade,
} from "@repo/app-types/entities/recording";
import type { RecordingAssessmentFeedback } from "@repo/app-types/entities/recordingAssessmentFeedback";
import type {
  AccountGradingRecording,
  GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { extractParagraphsFromValidation } from "@repo/app-types/helpers/gradeValidationHelpers";
import { sectionIsNotApplicableValueToDisplayValue } from "@repo/app-types/helpers/recordingAssessmentFeedbackHelpers";
import { isDefined } from "@repo/app-types/helpers/utilities";
import { type FC, useEffect, useMemo, useRef } from "react";
import { twMerge } from "tailwind-merge";
import {
  useLazyPlaybookGradingHighlightGradeQuery,
  usePlaybookGradingCallOutlineQuery,
  usePlaybookGradingSaveValidationMutation,
  usePlaybookGradingValidateGradeQuery,
} from "../../api/entities/performancePlaybook.js";
import { useRecordingToolActivitiesQuery } from "../../api/entities/recording.js";
import { AudioPlayer } from "../../components/audioPlayer/audioPlayer.js";
import { AudioPlayerProvider } from "../../components/audioPlayer/provider.js";
import { TranscriptScrollPane } from "../../components/transcript/transcriptScrollPane.js";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { useMatchQuoteToTranscript } from "../transcriptViewer/useMatchQuoteToTranscript.js";
import { CallNavigatorDropdown } from "./callNavigatorDropdown.js";
import { GradingAssessmentFeedbackButton } from "./gradingAssessmentFeedbackButton.js";
import { renderGradingValue } from "./gradingHelpers.js";
import { GradingSectionSummariesContainer } from "./gradingSectionSummariesContainer.js";
import { GradingTranscriptWithIndex } from "./gradingTranscriptWithIndex.js";
import { ParagraphsContainer } from "./paragraphsContainer.js";
import { maybeGetShareSnippetFromUrl } from "./snippetSharing/snippetSharingHelpers.js";
import { TranscriptVideoPanel } from "./transcriptVideoPanel.js";
import { useTranscriptOutlineSync } from "./useTranscriptOutlineSync.js";

//
// Props.
//

interface GradingValidationDialogProps {
  gradingId: GradingId;
  recordings: readonly AccountGradingRecording[];
  node: PlaybookCriterionNode | PlaybookSectionNode;
  grade: CriterionGrade | SectionGrade | undefined;
  feedback: RecordingAssessmentFeedback | undefined;
  /** Playbook of the grading; used by the criterion feedback button. */
  playbookId: PlaybookId;
  assessment: RecordingGradingAssessment;
  onCriteriaSelect: (criteriaId: string) => void;
}

//
// Component.
//

// TODO: Show transcript and highlight quotes for multiple recordings.
export const GradingValidationDialog: FC<GradingValidationDialogProps> = (
  props,
) => {
  const { gradingId, recordings, node, grade } = props;
  const { feedback, playbookId, assessment } = props;
  const hasTranscript = recordings.some((r) => r.transcript);

  // A criterion can be missing from the grading entirely — the grading run
  // skips criteria the model could not judge (insufficient context) or that
  // errored. There is nothing to validate then, and the API would 404, so
  // don't stream at all.
  const isUngradedCriterion = isPlaybookCriterionNode(node) && !grade;
  const isUngradedSection = isPlaybookSectionNode(node) && !grade;

  // Use the precomputed grading validation, if available
  const precomputedValidation = getPrecomputedGradingValidation(grade);

  // Stream the grading validation, if we don't have it precomputed
  const streamGradingValidationArgs =
    isPlaybookCriterionNode(node) &&
    !precomputedValidation &&
    !isUngradedCriterion
      ? {
          gradingId,
          sectionOrCriteriaId: node.id,
        }
      : undefined;
  const streamedValidation = useStreamedGradingValidation(
    streamGradingValidationArgs,
  );

  const { paragraphs, hasCompleted, hasFailed } = precomputedValidation ??
    streamedValidation ?? {
      paragraphs: [],
      hasCompleted: false,
      hasFailed: false,
    };

  const quotes = useMemo(() => {
    return paragraphs
      .map((p, i) => {
        if (p.type !== "quote") {
          return undefined;
        }

        if (!hasCompleted && !paragraphs[i + 1]) {
          return undefined;
        }

        return p;
      })
      .filter(isDefined);
  }, [paragraphs, hasCompleted]);

  const firstRecording = recordings.at(0);

  const {
    sortedRecordings,
    quoteMatches,
    handleQuotePress: onQuotePress,
    transcriptRef,
    transcriptContainerRef,
  } = useMatchQuoteToTranscript({
    recordings,
    quoteParagraphs: quotes,
    skipAutoScroll: !!maybeGetShareSnippetFromUrl(),
  });

  // Fetch tool activities for all recordings
  const recordingIds = sortedRecordings.map((r) => r.id);
  const { data: toolActivitiesByRecordingId } = useRecordingToolActivitiesQuery(
    recordingIds.length > 0 ? recordingIds : skipToken,
  );

  const { callPosition, scrollToCallPosition } = useTranscriptOutlineSync({
    transcriptContainerRef,
    sortedRecordings,
  });

  // Fetch call outline for overlay navigation
  const {
    data: callOutline,
    isLoading: isCallOutlineLoading,
    isError: isCallOutlineError,
  } = usePlaybookGradingCallOutlineQuery({ gradingId });

  return (
    <AudioPlayerProvider>
      <div className="grid h-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)] grid-rows-[minmax(0,1fr)_auto]">
        {/* Left pane - Grade validation */}
        <div className="relative overflow-y-auto border-r border-border-light p-4">
          <div className="-mt-1 mb-2 flex px-2 py-1">
            <GradingSectionSummariesContainer
              assessment={assessment}
              selectedCriteriaId={node.id}
              onCriteriaSelect={props.onCriteriaSelect}
              mode="clickable"
            />
          </div>
          <div className="rounded-md border border-border-light bg-white px-2 py-3">
            <h3 className="mb-3 px-2 text-sm font-semibold text-foreground">
              Analysis
            </h3>
            <div className="mt-1 mb-3 px-2">
              {isPlaybookCriterionNode(node) && grade && "value" in grade ? (
                <div className="flex items-center gap-2">
                  {renderGradingValue(node.grading_value_type, grade.value)}
                  {playbookId && firstRecording && (
                    <GradingAssessmentFeedbackButton
                      playbookId={playbookId}
                      node={node}
                      recordingId={firstRecording.id}
                      grade={grade}
                      feedback={feedback}
                    />
                  )}
                </div>
              ) : isPlaybookSectionNode(node) &&
                grade &&
                "is_not_applicable" in grade ? (
                <div className="font-medium text-foreground">
                  {sectionIsNotApplicableValueToDisplayValue(
                    grade.is_not_applicable,
                  )}
                </div>
              ) : null}
            </div>
            {isUngradedCriterion || isUngradedSection ? (
              <p className="px-2 text-sm text-muted-foreground">
                This {isUngradedSection ? "section" : "criterion"} was not
                graded for this customer interaction, so there is no analysis to
                show.
              </p>
            ) : isPlaybookSectionNode(node) && !precomputedValidation ? (
              <p
                className={twMerge(
                  "px-2 text-sm text-foreground",
                  PH_MASK_CLASS,
                )}
              >
                {grade?.commentary || "No section analysis is available."}
              </p>
            ) : hasFailed ? (
              <p className="px-2 text-sm text-muted-foreground">
                The analysis could not be loaded. Please try again.
              </p>
            ) : (
              <ParagraphsContainer
                recordings={sortedRecordings}
                paragraphs={paragraphs}
                hasCompleted={hasCompleted}
                quoteMatches={quoteMatches}
                onQuotePress={onQuotePress}
              />
            )}
          </div>
        </div>

        {/* Right pane - Transcript */}
        <div className="flex min-h-0 flex-col bg-white">
          <TranscriptVideoPanel recordings={sortedRecordings} />
          <div className="p-4">
            <CallNavigatorDropdown
              showCurrentKeyMoment
              recordings={sortedRecordings}
              callOutline={callOutline}
              isLoading={isCallOutlineLoading}
              isError={isCallOutlineError}
              callPosition={callPosition}
              onCallPositionRequest={scrollToCallPosition}
            />
          </div>
          <TranscriptScrollPane
            scrollContainerRef={transcriptContainerRef}
            className="px-4"
          >
            <div className="pb-4">
              {hasTranscript ? (
                <GradingTranscriptWithIndex
                  ref={transcriptRef}
                  recordings={sortedRecordings}
                  quoteMatches={quoteMatches}
                  scrollToCallPosition={scrollToCallPosition}
                  toolActivitiesByRecordingId={toolActivitiesByRecordingId}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  No transcript available
                </p>
              )}
            </div>
          </TranscriptScrollPane>
        </div>

        <div className="col-span-full">
          <AudioPlayer />
        </div>
      </div>
    </AudioPlayerProvider>
  );
};

function getPrecomputedGradingValidation(
  grade: CriterionGrade | SectionGrade | undefined,
) {
  const validation = grade?.validation;

  // Check whether it's an array, just in case development testing resulted in strings being stored
  return Array.isArray(validation)
    ? {
        paragraphs: validation,
        hasCompleted: true,
        hasFailed: false,
      }
    : undefined;
}

function useStreamedGradingValidation(
  args:
    | {
        gradingId: GradingId;
        sectionOrCriteriaId: string;
      }
    | undefined,
) {
  const { data } = usePlaybookGradingValidateGradeQuery(args ?? skipToken);
  const hasCompleted = Boolean(data?.hasCompleted);
  const hasFailed = Boolean(data?.hasFailed);
  const validation = data?.state.validation;

  // Trigger highlighting query after validation data is available
  const [triggerHighlighting, { data: highlightsResult }] =
    useLazyPlaybookGradingHighlightGradeQuery();
  const highlightsHasCompleted = highlightsResult?.hasCompleted ?? false;
  const highlightsHasFailed = highlightsResult?.hasFailed ?? false;
  const highlights = useMemo(
    () => highlightsResult?.state.highlights || [],
    [highlightsResult],
  );

  const gradingId = args?.gradingId;
  const sectionOrCriteriaId = args?.sectionOrCriteriaId;

  useEffect(() => {
    if (gradingId === undefined || !hasCompleted || !validation) {
      return;
    }

    triggerHighlighting(
      {
        gradingId,
        gradingValidation: validation,
      },
      false,
    );
  }, [hasCompleted, validation, gradingId, triggerHighlighting]);

  // Pre-process paragraphs with highlighting information
  const paragraphs = useMemo(() => {
    if (!validation) {
      return undefined;
    }

    return extractParagraphsFromValidation(validation, highlights);
  }, [validation, highlights]);

  // Save the validation when streaming is complete
  const [saveGradingValidation] = usePlaybookGradingSaveValidationMutation();
  const hasAlreadySavedStreamingResultRef = useRef(false);

  useEffect(() => {
    if (
      gradingId === undefined ||
      sectionOrCriteriaId === undefined ||
      !hasCompleted ||
      !highlightsHasCompleted ||
      !paragraphs ||
      hasAlreadySavedStreamingResultRef.current
    ) {
      return;
    }

    saveGradingValidation({
      gradingId,
      sectionOrCriteriaId,
      paragraphs,
    });

    hasAlreadySavedStreamingResultRef.current = true;
  }, [
    hasCompleted,
    highlightsHasCompleted,
    paragraphs,
    gradingId,
    sectionOrCriteriaId,
    saveGradingValidation,
  ]);

  if (hasFailed) {
    return { paragraphs: paragraphs ?? [], hasCompleted: true, hasFailed };
  }

  // Highlighting is an enhancement on top of the validation text, so a failed
  // highlighting stream still counts as done for rendering — quotes just stay
  // unhighlighted instead of pending forever. The save effect above
  // deliberately keeps requiring a real completion, so half-processed
  // validations aren't persisted.
  return paragraphs
    ? {
        paragraphs,
        hasCompleted:
          hasCompleted && (highlightsHasCompleted || highlightsHasFailed),
        hasFailed,
      }
    : undefined;
}
